import React from "react";
import { FloatContainer } from "../../FloatContainer/FloatContainer";
import "./InvoiceSplitPreviewModal.css";

/** Read-only settlement details for an invoice created before the Pix-only cutover. */
export type InvoiceSettlement = {
  grossAmountCents: number;
  netAmountCents: number;
  enterpriseResidualCents: number;
  rewardsAmountCents: number;
  contabileAmountCents: number;
  enterpriseCashReceivedCents: number;
  settlementStatus: string;
  enterpriseSettlementStatus: string;
};

export type InvoiceSplitPreviewModalProps = {
  open: boolean;
  invoiceId?: string;
  settlement?: InvoiceSettlement;
  onClose: () => void;
};

export function formatBRL(cents: number): string {
  const reais = cents / 100;
  return `R$ ${reais.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const enterpriseStatusLabel: Record<string, string> = {
  pending: "Aguardando liquidação",
  confirmed: "Confirmado no Stripe",
  delayed: "Repasse atrasado",
  liquidated: "Recebido em conta",
  refunded: "Estornado",
  failed: "Falha no repasse",
};

/**
 * @story args { invoiceId: "inv-demo-1", settlement: { grossAmountCents: 10500, netAmountCents: 10000, enterpriseResidualCents: 5000, rewardsAmountCents: 3000, contabileAmountCents: 2000, enterpriseCashReceivedCents: 0, settlementStatus: "paid", enterpriseSettlementStatus: "confirmed" } }
 * @story state Liquidated { invoiceId: "inv-demo-2", settlement: { grossAmountCents: 10500, netAmountCents: 10000, enterpriseResidualCents: 5000, rewardsAmountCents: 3000, contabileAmountCents: 2000, enterpriseCashReceivedCents: 5000, settlementStatus: "liquidated", enterpriseSettlementStatus: "liquidated" } }
 * @story state AwaitingReconciliation { invoiceId: "inv-demo-3", settlement: undefined }
 */
export function InvoiceSplitPreviewModal(props: InvoiceSplitPreviewModalProps) {
  if (!props.open) return null;

  const settlement = props.settlement;

  return (
    <div data-testid="invoice-split-modal" data-testid-alias="invoice-modal">
      <FloatContainer close={props.onClose} className="invoiceSplitPreview">
        <div className="invoiceSplitPreviewInner">
          <h2 className="invoiceSplitPreviewTitle">Detalhamento da liquidação</h2>
          {props.invoiceId && (
            <div className="invoiceSplitPreviewSubtitle" data-testid="invoice-split-invoice-id">
              Fatura #{props.invoiceId}
            </div>
          )}

          {settlement ? (
            <>
              <div className="invoiceSplitPreviewRows">
                <div className="invoiceSplitPreviewRow invoiceSplitPreviewRowGross">
                  <span className="invoiceSplitPreviewLabel">Cobrança bruta</span>
                  <span
                    className="invoiceSplitPreviewValue"
                    data-testid="invoice-split-gross-amount"
                  >
                    {formatBRL(settlement.grossAmountCents)}
                  </span>
                </div>
                <div className="invoiceSplitPreviewRow invoiceSplitPreviewRowNet">
                  <span className="invoiceSplitPreviewLabel">Saldo líquido Stripe</span>
                  <span className="invoiceSplitPreviewValue" data-testid="invoice-split-net-amount">
                    {formatBRL(settlement.netAmountCents)}
                  </span>
                </div>
                <div className="invoiceSplitPreviewRow">
                  <span className="invoiceSplitPreviewLabel">Residual Enterprise</span>
                  <span
                    className="invoiceSplitPreviewValue"
                    data-testid="invoice-split-enterprise-amount"
                  >
                    {formatBRL(settlement.enterpriseResidualCents)}
                  </span>
                </div>
                <div className="invoiceSplitPreviewRow">
                  <span className="invoiceSplitPreviewLabel">Contábil</span>
                  <span
                    className="invoiceSplitPreviewValue"
                    data-testid="invoice-split-contabile-amount"
                  >
                    {formatBRL(settlement.contabileAmountCents)}
                  </span>
                </div>
                <div className="invoiceSplitPreviewRow">
                  <span className="invoiceSplitPreviewLabel">Recompensas</span>
                  <span
                    className="invoiceSplitPreviewValue"
                    data-testid="invoice-split-rewards-amount"
                  >
                    {formatBRL(settlement.rewardsAmountCents)}
                  </span>
                </div>
                <div className="invoiceSplitPreviewRow invoiceSplitPreviewRowCash">
                  <span className="invoiceSplitPreviewLabel">
                    Caixa Enterprise recebido
                    <small>
                      {enterpriseStatusLabel[settlement.enterpriseSettlementStatus] ??
                        settlement.enterpriseSettlementStatus}
                    </small>
                  </span>
                  <span
                    className="invoiceSplitPreviewValue"
                    data-testid="invoice-split-cash-amount"
                  >
                    {formatBRL(settlement.enterpriseCashReceivedCents)}
                  </span>
                </div>
              </div>

              <div className="invoiceSplitPreviewFooter">
                <span className="invoiceSplitPreviewFooterLabel">Líquido distribuído</span>
                <span className="invoiceSplitPreviewFooterValue" data-testid="invoice-split-total">
                  {formatBRL(settlement.netAmountCents)}
                </span>
              </div>
            </>
          ) : (
            <div className="invoiceSplitPreviewUnavailable" data-testid="invoice-split-unavailable">
              A divisão ainda não foi reconciliada. Os valores serão exibidos quando o Stripe
              confirmar a liquidação.
            </div>
          )}

          <button
            type="button"
            className="invoiceSplitPreviewClose"
            data-testid="invoice-split-close"
            onClick={props.onClose}
          >
            Fechar
          </button>
        </div>
      </FloatContainer>
    </div>
  );
}
